import React, { useContext } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
    BudgetProposalT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import { InscriptionGenericPhaseFieldT } from 'routes/BudgetModule/shared/entities/inscription';
import { getFieldForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { Box, styled } from '@mui/material';
import { BudgetPhases } from 'business/constants';
import { useScrollessRef } from 'hooks/helpers';
import { getInscriptionInformation } from '../dataHelpers';
import { UpdateDisabledTooltip } from '../UpdateDisabledTooltip';
import TextFieldWithDebounce from 'ui/WithDebounce/TextFieldWithDebounce';
import { PHASE_ID } from 'routes/BudgetModule/config/constants';

const StyTextFieldCellRendered = styled(Box, {
    name: 'StyTextFieldCellRendered',
    shouldForwardProp: (prop) => prop !== 'isRejected',
})<{ isRejected: boolean }>(({ isRejected, theme }) => ({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    // we're not refactoring <Component /> with styled, because WithDebounce doesn't support wrapping a styled component
    '& .MuiFormControl-root': {
        height: '33px',
        padding: 0,
        '& .MuiInputBase-root': {
            paddingBottom: 0,
            paddingTop: 0,
            height: '100%',
        },
        '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: theme.palette.lightGrey,
        },
        '& .MuiInputBase-input': {
            ...(isRejected
                ? {
                      textDecorationLine: 'line-through',
                      textDecorationThickness: '0.1em',
                  }
                : {}),
            fontSize: '11px',
            height: 'inherit',
            lineHeight: '16px',
        },
    },
}));

export type TextFieldCellRendererParams = CellRendererParams<
    InscriptionWithMetadata,
    | {
          isRejected: boolean;
          text: string;
      }
    | number
> & {
    setValue?: (value: string) => void;
    budgetProposals: BudgetProposalT[];
    isEnabled?: (arg1: BudgetPhases) => boolean;
    defaultValue?: number;
    valueTransformer?: (input: string) => number;
    textFieldProps: TextFieldProps;
    onInscriptionUpdate?: (arg1: {
        id: number | string;
        phase: BudgetPhases;
        data: unknown; // body of request
    }) => void;
};

export default function TextFieldCellRenderer({
    data,
    value,
    colDef,
    setValue,
    budgetProposals,
    isEnabled,
    defaultValue,
    valueTransformer,
    textFieldProps = {} as FlowAnyObject,
    onInscriptionUpdate,
    node,
}: TextFieldCellRendererParams): React.ReactElement {
    const statusesSettings = useContext(StatusSettingsContext);

    // We need this ref and to disable input change with mouse wheel on
    // the ref of MUI's TextField component
    const textFieldRef = useScrollessRef();
    if (!value) {
        return <span />;
    }

    const {
        budgetProposal,
        disabledByResource,
        isSupplementaryNotEditable,
        canEdit,
        phaseEditable,
    } = getInscriptionInformation(data, budgetProposals, statusesSettings);

    const hideToolTip = node.group || false;

    const Component = canEdit ? TextFieldWithDebounce : TextField;
    const getFieldValue = () => {
        if (typeof value === 'object' && 'text' in value) {
            return value.text === 'unknown' ? '' : value.text;
        }
        return value;
    };
    const fieldValue = getFieldValue();
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = event.target.value;
        if (!newValue && defaultValue !== undefined) {
            // Product requirement: we need to replace blanks by 0 for numeric columns
            newValue = defaultValue.toString();
        }

        if (!budgetProposal) return;
        if (onInscriptionUpdate && data)
            onInscriptionUpdate({
                id: data.inscription[PHASE_ID] as string | number,
                phase: budgetProposal.phase,
                data: {
                    [getFieldForPhase(
                        budgetProposal.phase,
                        colDef?.field as InscriptionGenericPhaseFieldT,
                    )!.split('.')[1]]: valueTransformer
                        ? valueTransformer(newValue)
                        : newValue,
                },
            });
        setValue?.(newValue);
    };
    return (
        <StyTextFieldCellRendered
            isRejected={typeof value === 'object' && value.isRejected}
        >
            <UpdateDisabledTooltip
                disabledByResource={disabledByResource}
                isSupplementaryNotEditable={isSupplementaryNotEditable}
                disabledByRights={!canEdit}
                disabledByPhase={!phaseEditable}
                hidden={hideToolTip}
                by={budgetProposal?.by}
            >
                <Component
                    className="mui-text-field-input"
                    value={fieldValue}
                    disabled={
                        !canEdit ||
                        (budgetProposal &&
                            isEnabled &&
                            !isEnabled(budgetProposal.phase))
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={onChange}
                    {...textFieldProps}
                    ref={textFieldRef}
                />
            </UpdateDisabledTooltip>
        </StyTextFieldCellRendered>
    );
}
